import { Add, Edit } from "@mui/icons-material";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List, ListItemButton, ListItemIcon, Typography} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectEconomics } from "../reducers/economicsSlice";
import { EditPricesChart } from "./EditPricesChart";
import { EditPriceData } from "./EditPriceData";
import { NewPriceDeck } from "./NewPriceDeck";

export const EditPrices = ({ handleEditPricesClose }) => {
  
  const economics = useSelector(selectEconomics);
  
  const [editPriceVisible, setEditPriceVisible] = useState(false);
  const [newPriceDeckVisible, setNewPriceDeckVisible] = useState(false);

  const [priceDecks] = useState(economics?.prices && Object.keys(economics?.prices));
  const [currentPriceDeckName, setCurrentPriceDeckName] = useState(priceDecks?.[0]);
  const [currentCommodityName, setCurrentCommodityName] = useState(Object.keys(economics?.prices?.[currentPriceDeckName]?.[0])?.filter(p => p !== 'date' && p !== 'cadToUsd')?.[0]);

  const calculatePriceData = useCallback(() => {
    return (economics?.prices?.[currentPriceDeckName]?.filter(p => Number(p[currentCommodityName]) > 0)?.map(p => {
      return {date: new Date(p.date)?.toISOString()?.split('T')?.[0], price: Number(p[currentCommodityName])};
    }));
  }, [economics?.prices, currentPriceDeckName, currentCommodityName]);

  const [priceData, setPriceData] = useState(() => calculatePriceData(currentCommodityName));

  useEffect(() => {
    setPriceData(calculatePriceData(currentCommodityName));
  }, [currentCommodityName, currentPriceDeckName, economics?.prices, calculatePriceData]);

  const [checked, setChecked] = useState([currentCommodityName]);

  return (
    <Dialog 
      open={true} 
      onClose={handleEditPricesClose}
      fullWidth={true}
      maxWidth="xl"
    >
      <DialogTitle>Edit Prices</DialogTitle>
      <DialogContent style={{display: 'flex', overflow: 'hidden', width: '100%', height: '600px'}}>
        <div style={{display: 'flex', overflow: 'scroll', width: '100%', height: '100%'}}>
          <div style={{width: '400px', borderRight: '1px solid lightgrey', overflow: 'scroll'}}>
            
            <div style={{marginBottom: 10}}>
              <Typography variant="h6" noWrap style={{marginBottom: 10}}>Price Decks</Typography>
              <Button variant="contained" startIcon={<Add />} size="small" style={{marginLeft: 0}} onClick={() => setNewPriceDeckVisible(true)} disabled>New Price Deck</Button>
            </div>

            <Divider />

            <List style={{height: '600px', overflow: 'scroll'}}>
              {priceDecks?.map(priceDeck => {
                return (
                  <ListItemButton 
                    key={priceDeck} 
                    onClick={() => {
                      setCurrentPriceDeckName(priceDeck);
                    }} 
                    style={{
                      backgroundColor: priceDeck === currentPriceDeckName && 'lightgrey', 
                      margin: 0, 
                      paddingLeft: 10, 
                      width: '100%', 
                      borderBottom: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      height: '50px'
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <Typography noWrap style={{textOverflow: 'ellipsis', width: '240px'}}>{priceDeck}</Typography>
                    </div>
                  </ListItemButton>
                )
              })}

            </List>
          </div>

          <div style={{width: '400px', borderRight: '1px solid lightgrey', overflow: 'scroll'}}>
            
            <div style={{marginBottom: 10}}>
              <Typography variant="h6" noWrap style={{marginBottom: 10, marginLeft: 10}}>{currentPriceDeckName}</Typography>
              <Button variant="contained" startIcon={<Add />} size="small" style={{marginLeft: 10}} disabled>New Commodity</Button>
            </div>

            <Divider />

            <List style={{height: '600px', overflow: 'scroll'}}>
              {economics?.prices?.[currentPriceDeckName]?.[0]
                && Object.keys(economics?.prices?.[currentPriceDeckName]?.[0])?.filter(p => p !== 'date' && p !== 'cadToUsd')?.map(commodity => {
                  return (
                    <ListItemButton
                      key={commodity} 
                      onClick={e => {
                        setCurrentCommodityName(commodity);
                        handleCheck(e, commodity)
                      }} 
                      style={{
                        backgroundColor: commodity === currentCommodityName && 'lightgrey', 
                        margin: 0, 
                        padding: 0, 
                        width: '100%', 
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        height: '50px'
                      }}
                    >
                      <ListItemIcon style={{padding: 0, margin: 0}}>
                        <Checkbox
                          checked={checked.indexOf(commodity) !== -1}
                          onChange={(e) => handleCheck(e, commodity)}
                          tabIndex={-1}
                          disableRipple
                          color="primary"
                          size="small"
                          style={{padding: 0, marginLeft: 10}}
                        />
                      </ListItemIcon>
                      <div style={{textOverflow: 'ellipsis', overflow: 'hidden', padding: 0, margin: -20, width: 175}}>
                        <Typography noWrap style={{}}>{commodity}</Typography>
                      </div>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditPriceVisible(true);
                        }} 
                        style={{position: 'absolute', right: 10}}
                        disabled
                      ><Edit fontSize="14px"/></IconButton>
                    </ListItemButton>
                  )
                })
              }
            </List>
          </div>

          <div style={{width: '100%', height: '600px', overflow: 'scroll'}}>
            <EditPricesChart priceDeck={currentPriceDeckName} checked={checked} />
          </div>
        </div>

        {editPriceVisible && <EditPriceData priceData={priceData} setPriceData={setPriceData} setEditPriceVisible={setEditPriceVisible} />}
        {newPriceDeckVisible && <NewPriceDeck setNewPriceDeckVisible={setNewPriceDeckVisible} />}

      </DialogContent>
      <DialogActions>
        <Button onClick={handleEditPricesClose} color="primary">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => {}} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS
  
  function handleCheck(e, commodity) {
    try {
      e.stopPropagation();
      e.preventDefault();

      if (checked?.indexOf(commodity) !== -1) {
        setChecked(checked?.filter(c => c !== commodity));
      } else {
        setChecked([...checked, commodity]);
      };

    } catch (error) {
      console.log(error);
    };
  };
};